import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import { GenericResponseType } from "../../types/response.ts";
import {
  normalizeCommissionProperties,
  normalizeDate,
  normalizeInsuranceCompanyProperties,
  normalizeNewPolicyProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import { Commission } from "../../types/insurance.ts";
import { COMMON_API_URL } from "../../constants/urls";

interface PolicyType {
  policyId: string;
  baseType: string;
  name: string;
}

type KeyConversionType = "camelCase" | "topLevel" | "none";

interface FetchOptions {
  convertKeys?: KeyConversionType;
}

const fetchData = async (
  jwt: string,
  endpoint: string,
  additionalParams?: any,
  options: FetchOptions = {}
): Promise<any> => {
  const { convertKeys = "camelCase" } = options;

  const response = await fetch(`${COMMON_API_URL}/${endpoint}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...additionalParams }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  let data: GenericResponseType;

  switch (convertKeys) {
    case "topLevel":
      data = CapitalizationUtil.convertTopLevelKeysToCamelCase(
        await response.json()
      );
      break;
    case "none":
      data = await response.json();
      break;
    default:
      data = CapitalizationUtil.convertKeysToCamelCase(await response.json());
  }

  if (data.error) {
    throw new Error(data.error);
  }

  if (data.result) {
    return data.result;
  } else {
    return data.message;
  }
};

const fetchCommissions = async (
  jwt: string,
  additionalParams?: any
): Promise<Commission[]> => {
  const response = await fetch(`${COMMON_API_URL}/getComm`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...additionalParams }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = CapitalizationUtil.convertTopLevelKeysToCamelCase(
    await response.json()
  );

  if (data.error) {
    throw new Error(data.error);
  }

  if (data.result && data.result !== "0") {
    return (data.result as []).map((commission: object) => {
      const normalizedCommission = normalizeCommissionProperties(commission);
      return {
        ...normalizedCommission,
        startDate: normalizeDate(normalizedCommission.startDate),
      };
    });
  } else if (data.message) {
    return data.message;
  } else {
    return [];
  }
};

const saveInsuranceCompanyReport = async (
  jwt: string,
  insuranceCompany: string,
  reportNumber: string,
  date: string,
  policies: any[]
): Promise<{ message: string; result: any[] }> => {
  const response = await fetch(`${COMMON_API_URL}/writeRpt_ZK`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      InsComp: insuranceCompany,
      Document: reportNumber,
      Doc_Date: date,
      Pol_Arr: policies,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.Error) {
    throw new Error(data.Error);
  }

  return { message: data.Message, result: data.Result };
};

const fetchBasePolicies = async (jwt: string): Promise<any> => {
  return fetchData(jwt, "getBaseInsType", {}, { convertKeys: "topLevel" });
};

const getAgencyDataByCompany = async (jwt: string, companyId: string) => {
  const data = await fetchData(
    jwt,
    "getAgencyData",
    { CID: companyId },
    { convertKeys: "topLevel" }
  );

  return normalizeInsuranceCompanyProperties(data.agencyData);
};

const getPolicyDataByPolicyType = async (
  jwt: string,
  policyType: string
): Promise<PolicyType> => {
  const data = await fetchData(
    jwt,
    "getInsTypeData",
    { PID: policyType },
    { convertKeys: "topLevel" }
  );

  return normalizeNewPolicyProperties(data) as PolicyType;
};

const buildParams = (params: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) => value !== "" && value !== undefined
    )
  );
};

const InsuranceService = {
  getAllAgents: (jwt: string) => fetchData(jwt, "getAgents", { all: false }),

  getBaseInsuranceCompanies: (jwt: string) => fetchData(jwt, "getBaseInsComp"),

  getAllInsuranceCompanies: (jwt: string, shouldGetAll = false, base = "") =>
    fetchData(jwt, "getInsComp", { all: shouldGetAll, base }),

  getAllPolicyTypes: (jwt: string, base = "") =>
    fetchData(jwt, "getInsType", buildParams({ base })),

  getAllOffices: (jwt: string, shouldGetAllOffices = false) =>
    fetchData(jwt, "getOffices", { all: shouldGetAllOffices }),

  writePolicyData: (jwt: string, policyData: object) =>
    fetchData(jwt, "InsType_write", { ...policyData }),

  getBasePolicies: (jwt: string) => fetchBasePolicies(jwt),

  getPolicyDataByPolicyType: (jwt: string, policyType: string) =>
    getPolicyDataByPolicyType(jwt, policyType),

  getAgencyDataByCompany: (jwt: string, companyId: string) =>
    getAgencyDataByCompany(jwt, companyId),

  writeAgencyData: (jwt: string, agencyData: object) =>
    fetchData(jwt, "agency_write", agencyData),

  getCommissions: (
    jwt: string,
    officeOrAgentId: string,
    policyType: string,
    insuranceCompany: string
  ) =>
    fetchCommissions(jwt, {
      AID: officeOrAgentId,
      PID: policyType,
      CID: insuranceCompany,
    }),

  saveCommissions: (
    jwt: string,
    officeOrAgentId: string,
    policyType: string,
    insuranceCompany: string,
    percentage: string,
    beginningDate: string,
    foreignPercentage: string
  ) =>
    fetchData(jwt, "comm_write", {
      AID: officeOrAgentId,
      PID: policyType,
      CID: insuranceCompany,
      PERC: percentage,
      DateBeg: revertNormalizeDate(beginningDate),
      OTH: foreignPercentage === "" ? "0" : foreignPercentage,
    }),

  getPolicyReportNumber: (jwt: string, insuranceCompany: string) =>
    fetchData(jwt, "getInsCompCntr", { InsComp: insuranceCompany }),

  calculateOffer: (jwt: string, formState: object) =>
    fetchData(jwt, "calculateOffer", formState),

  saveInsuranceCompanyReport,
};

export default InsuranceService;
